import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import './SessionConflictNotification.css';

/**
 * SessionConflictNotification component
 * Shows notification when session conflict is detected
 * @param {Object} props - Component props
 * @param {Function} props.onDismiss - Callback when notification is dismissed
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Session conflict notification component
 */
const SessionConflictNotification = ({ onDismiss, className = '' }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  useEffect(() => {
    const handleSessionConflict = (event) => {
      console.log('🔍 SessionConflictNotification: Received session conflict event:', event.detail);
      setConflictData(event.detail);
      setIsVisible(true);
    };

    window.addEventListener('auth:sessionConflict', handleSessionConflict);
    
    return () => {
      window.removeEventListener('auth:sessionConflict', handleSessionConflict);
    };
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setConflictData(null);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleLoginRedirect = () => {
    window.location.href = '/login';
  };

  if (!isVisible || !conflictData) {
    return null;
  }

  return (
    <div className={`session-conflict-notification ${className}`} role="alert">
      <div className="session-conflict-notification__content">
        <div className="session-conflict-notification__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" 
              fill="currentColor"
            />
            <path 
              d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" 
              fill="currentColor"
              opacity="0.3"
            />
          </svg>
        </div>
        
        <div className="session-conflict-notification__text">
          <h3 className="session-conflict-notification__title">
            {conflictData.reason === 'session_conflict' 
              ? t('auth.sessionConflict.title', 'Друга сесия е активна')
              : t('auth.sessionTerminated.title', 'Сесията е прекратена')
            }
          </h3>
          <p className="session-conflict-notification__message">
            {conflictData.message || 
              (conflictData.reason === 'session_conflict' 
                ? t('auth.sessionConflict.message', 'Друга сесия е активна на този акаунт. Моля влезте отново.')
                : t('auth.sessionTerminated.message', 'Сесията ви е прекратена. Моля влезте отново.')
              )
            }
          </p>
        </div>

        <div className="session-conflict-notification__actions">
          <button
            type="button"
            className="session-conflict-notification__button session-conflict-notification__button--primary"
            onClick={handleLoginRedirect}
          >
            {t('auth.sessionConflict.loginButton', 'Вход')}
          </button>
          <button
            type="button"
            className="session-conflict-notification__button session-conflict-notification__button--secondary"
            onClick={handleDismiss}
          >
            {t('common.close', 'Затвори')}
          </button>
        </div>

        <button
          type="button"
          className="session-conflict-notification__close"
          onClick={handleDismiss}
          aria-label={t('common.close', 'Затвори')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M12 4L4 12M4 4L12 12" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SessionConflictNotification;
